import React, { useState, useEffect } from 'react';
import Modal from './Modal';

/**
 * Prompt dialog with one or more controlled fields. Replaces window.prompt().
 *
 * Props:
 *   isOpen       {boolean}
 *   title        {string}
 *   fields       {Array<{ name, label, type?, placeholder?, required? }>}
 *   confirmLabel {string}    default 'Submit'
 *   loading      {boolean}
 *   onConfirm    {function}  called with { [name]: value } map
 *   onCancel     {function}
 */
const PromptDialog = ({
  isOpen,
  title,
  fields = [],
  confirmLabel = 'Submit',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [values, setValues] = useState({});

  // Reset values whenever the dialog opens
  useEffect(() => {
    if (isOpen) {
      setValues(Object.fromEntries(fields.map((f) => [f.name, ''])));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(values);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="btn-secondary disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="prompt-dialog-form"
            disabled={loading}
            className="btn-primary disabled:opacity-50"
          >
            {loading ? 'Please wait…' : confirmLabel}
          </button>
        </>
      }
    >
      <form id="prompt-dialog-form" onSubmit={handleSubmit} className="space-y-4">
        {fields.map((field) => (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.type || 'text'}
              className="input"
              placeholder={field.placeholder || ''}
              required={field.required}
              value={values[field.name] || ''}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, [field.name]: e.target.value }))
              }
            />
          </div>
        ))}
      </form>
    </Modal>
  );
};

export default PromptDialog;
